import { useState } from 'react'
import { Link } from 'react-router-dom'
import SiteLayout from '@/components/feature/SiteLayout'
import { usePageSeo } from '@/hooks/usePageSeo'

const faqs = [
  {
    question: '网站上的影视作品可以在线观看吗？',
    answer:
      '当前站点为影视内容展示演示，详情页的播放区域仅用于演示播放器界面，并未接入真实视频源，因此无法实际播放。后续如有正式片源，会在详情页明确标注。',
  },
  {
    question: '作品信息是真实数据吗？',
    answer:
      '不是。全站作品名称、封面、剧情简介、导演与演员信息均为示例内容，用于演示页面结构与浏览体验，与实际影视作品无关。',
  },
  {
    question: '收藏的内容保存在哪里？',
    answer:
      '收藏状态保存在当前浏览器的本地存储中，刷新页面后仍然保留，不会上传到服务器，也不会跨设备同步。清除浏览器数据后收藏会一并清除。',
  },
  {
    question: '如何快速找到某一类作品？',
    answer:
      '可以在顶部分类入口进入分类页，使用题材类型、上映年份与排序筛选；也可以直接使用搜索功能，输入标题、类型或演职人员姓名进行模糊匹配。',
  },
]

export default function About() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  usePageSeo(
    '关于我们 - 麻豆传媒在线影视内容门户',
    '了解麻豆传媒在线的站点定位、内容说明、版权与免责声明、联系方式与常见问题，本站为影视内容展示演示站点。',
    '麻豆传媒在线,关于我们,版权声明,免责声明,影视门户,常见问题',
  )

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="page-shell py-10 md:py-14">
          <nav aria-label="面包屑导航" className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-accent-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <span className="text-foreground-800">关于我们</span>
          </nav>

          <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-8">
              <p className="flex items-center gap-2 text-[11px] tracking-wide text-accent-700">
                <span className="h-1 w-1 rounded-full bg-accent-500" />
                ABOUT US
              </p>
              <h1 className="mt-2 text-2xl font-bold leading-tight tracking-tight text-foreground-950 md:text-4xl">
                关于麻豆传媒在线
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
                这是一个专注于影视作品展示与分类浏览的在线门户示例站点。我们把作品库、分类筛选、
                搜索与详情信息整理成一条清晰的浏览路径，帮助观众更快找到感兴趣的电影、电视剧与短片。
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 lg:col-span-4">
              {[
                { value: '3', label: '内容分类' },
                { value: '9', label: '题材类型' },
                { value: '12', label: '示例作品' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg border border-background-200 bg-background-50 p-4 text-center"
                >
                  <p className="font-label text-2xl leading-none text-primary-800">{item.value}</p>
                  <p className="mt-2 text-[10px] text-foreground-500">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="page-shell mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="flex flex-col gap-8 lg:col-span-8">
          <section aria-labelledby="position-title">
            <h2 id="position-title" className="text-lg font-bold text-foreground-950">
              站点定位
            </h2>
            <div className="mt-3 space-y-3 rounded-lg border border-background-200 bg-background-50 p-5 text-sm leading-7 text-foreground-700">
              <p>
                麻豆传媒在线面向喜欢在线浏览影视作品内容的观众，提供作品展示、分类浏览、关键词搜索
                与详情查看等基础能力。页面结构围绕"先发现、再筛选、后了解"的顺序展开，尽量减少查找成本。
              </p>
              <p>
                分类体系按电影、电视剧、短片三个维度组织，并在每个分类下提供题材类型、上映年份与热度排序
                的多维筛选；详情页集中展示封面、元信息、剧情概述、演职人员与相关推荐。
              </p>
            </div>
          </section>

          <section aria-labelledby="copyright-title">
            <h2 id="copyright-title" className="text-lg font-bold text-foreground-950">
              版权声明与免责声明
            </h2>
            <div className="mt-3 space-y-4 rounded-lg border border-background-200 bg-background-50 p-5 text-sm leading-7 text-foreground-700">
              <div>
                <h3 className="text-sm font-medium text-foreground-900">版权声明</h3>
                <p className="mt-1.5 text-foreground-600">
                  本站为演示站点，站内所有影视作品名称、封面图、剧情简介、评分与演职人员信息均为示例内容，
                  不指向任何真实作品或权利人。页面中的图片为演示用生成素材，仅用于展示版式与视觉效果。
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-foreground-900">免责声明</h3>
                <p className="mt-1.5 text-foreground-600">
                  本站不提供任何真实视频播放服务，详情页播放区域仅作界面演示，请勿据此判断片源可用性。
                  如未来引入正式内容，将依法取得授权并在页面中明确标注来源。
                </p>
              </div>
              <div className="rounded-md border border-accent-200 bg-accent-50 p-4 text-xs leading-6 text-accent-900">
                <i className="ri-error-warning-line mr-1.5" />
                本站不涉及用户登录、支付与任何形式的会员服务，也不会收集个人敏感信息。
              </div>
            </div>
          </section>

          <section aria-labelledby="faq-title">
            <h2 id="faq-title" className="text-lg font-bold text-foreground-950">
              常见问题
            </h2>
            <div className="mt-3 divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {faqs.map((faq, index) => {
                const open = openIndex === index
                return (
                  <div key={faq.question}>
                    <button
                      type="button"
                      onClick={() => setOpenIndex(open ? null : index)}
                      aria-expanded={open}
                      className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-4 text-left"
                    >
                      <span className="text-sm font-medium text-foreground-900">{faq.question}</span>
                      <i
                        className={`shrink-0 text-foreground-500 transition-transform ${
                          open ? 'ri-subtract-line' : 'ri-add-line'
                        }`}
                      />
                    </button>
                    {open && (
                      <p className="px-5 pb-5 text-xs leading-6 text-foreground-600">{faq.answer}</p>
                    )}
                  </div>
                )
              })}
            </div>
          </section>
        </div>

        <aside className="lg:col-span-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-sm font-semibold text-foreground-950">联系我们</h2>
            <p className="mt-2 text-xs leading-6 text-foreground-500">
              对页面结构、内容展示方式有建议，可以通过以下示例方式联系我们。
            </p>
            <ul className="mt-4 space-y-3 text-xs text-foreground-700">
              <li className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className="ri-mail-line" />
                </span>
                contact@madou-demo.com
              </li>
              <li className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className="ri-time-line" />
                </span>
                工作日 10:00 - 18:00（示例）
              </li>
              <li className="flex items-start gap-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className="ri-map-pin-line" />
                </span>
                线上演示站点，无实体办公地址
              </li>
            </ul>
            <p className="mt-4 rounded-md bg-background-100 px-3 py-2 text-[11px] leading-5 text-foreground-500">
              以上联系方式为演示信息，请勿用于真实业务往来。
            </p>
          </div>

          <div className="mt-4 rounded-lg bg-primary-950 p-5">
            <h2 className="text-sm font-semibold text-background-50">开始浏览</h2>
            <p className="mt-2 text-xs leading-6 text-background-300">
              从分类页按题材与年份筛选，或者在搜索页输入关键词，快速定位想看的作品。
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <Link
                to="/category"
                className="flex items-center justify-between whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-xs font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                进入影视分类
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/search"
                className="flex items-center justify-between whitespace-nowrap rounded-md border border-background-50/25 px-4 py-2.5 text-xs text-background-100 transition-colors hover:border-background-50/60"
              >
                搜索影视作品
                <i className="ri-search-line" />
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </SiteLayout>
  )
}